import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import {
  Box,
  Button,
  CloseButton,
  Group,
  Highlight,
  Loader,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core';
import { IconDownload, IconSearch, IconTextCaption } from '@tabler/icons-react';
import { useRemoteText } from '@/hooks/useSessionMedia';
import { cuesToPlainText, findActiveCueIndex, formatCueTime, parseVtt, type VttCue } from '@/utils/vtt';
import { saveTextFile } from './CaptionsTrack';
import type { PlaybackController } from './playbackController';
import type { TranscriptSource } from './transcriptSource';

export type { TranscriptSource } from './transcriptSource';

export interface TranscriptPanelProps extends TranscriptSource {
  /** Active media element link, used to follow along and to seek on click. */
  controller?: PlaybackController | null;
  /** File name stem for "Download transcript". */
  fileNameBase?: string;
  /** Fixed-height variant for inline use (KB steps). */
  compact?: boolean;
}

const NO_SUBSCRIBE = () => () => {};
const USER_SCROLL_PAUSE_MS = 5_000;

/**
 * Transcript list with timestamps: highlights the line being spoken, click a
 * line to jump there, search, and download as a .txt file.
 */
export function TranscriptPanel({
  status,
  vttUrl,
  txtUrl,
  controller,
  fileNameBase = 'transcript',
  compact = false,
}: TranscriptPanelProps) {
  const ready = status === 'ready';
  const vtt = useRemoteText(ready ? vttUrl : null);
  const cues = useMemo(() => parseVtt(vtt.data), [vtt.data]);
  // Plain text is only needed when there are no usable cues.
  const needTxt = ready && !!txtUrl && (!vttUrl || (!vtt.isLoading && cues.length === 0));
  const txt = useRemoteText(needTxt ? txtUrl : null);

  const [query, setQuery] = useState('');
  const [downloading, setDownloading] = useState(false);
  const q = query.trim().toLowerCase();

  const subscribe = useMemo(
    () => (controller ? (cb: () => void) => controller.subscribe(cb) : NO_SUBSCRIBE),
    [controller],
  );
  const activeIndex = useSyncExternalStore(subscribe, () =>
    controller ? findActiveCueIndex(cues, controller.getCurrentTime()) : -1,
  );

  const visible = useMemo(() => {
    const all = cues.map((cue, index) => ({ cue, index }));
    return q ? all.filter(({ cue }) => cue.text.toLowerCase().includes(q)) : all;
  }, [cues, q]);

  // Follow the active line unless the viewer is searching or just scrolled.
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef(new Map<number, HTMLElement>());
  const lastUserScrollRef = useRef(0);
  useEffect(() => {
    if (q || activeIndex < 0) return;
    if (Date.now() - lastUserScrollRef.current < USER_SCROLL_PAUSE_MS) return;
    const viewport = viewportRef.current;
    const row = rowRefs.current.get(activeIndex);
    if (!viewport || !row) return;
    const vp = viewport.getBoundingClientRect();
    const r = row.getBoundingClientRect();
    if (r.top < vp.top || r.bottom > vp.bottom) {
      viewport.scrollBy({ top: r.top - vp.top - vp.height / 3, behavior: 'smooth' });
    }
  }, [activeIndex, q]);

  const registerRow = useCallback((index: number, el: HTMLElement | null) => {
    if (el) rowRefs.current.set(index, el);
    else rowRefs.current.delete(index);
  }, []);

  const markUserScroll = useCallback(() => {
    lastUserScrollRef.current = Date.now();
  }, []);

  const seek = useCallback(
    (cue: VttCue) => {
      lastUserScrollRef.current = 0;
      controller?.seek(cue.start);
    },
    [controller],
  );

  const download = async () => {
    setDownloading(true);
    let text: string | null = null;
    if (txtUrl) {
      try {
        const res = await fetch(txtUrl, { mode: 'cors', credentials: 'omit' });
        if (res.ok) text = await res.text();
      } catch {
        /* fall back to the cues */
      }
    }
    if (!text && cues.length > 0) text = cuesToPlainText(cues);
    if (!text && txt.data) text = txt.data;
    if (text) saveTextFile(text, `${fileNameBase}-transcript.txt`);
    setDownloading(false);
  };

  const frame = (body: ReactNode) => (
    <Box
      style={
        compact
          ? { display: 'flex', flexDirection: 'column' }
          : { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }
      }
    >
      {body}
    </Box>
  );

  if (!ready) {
    return frame(
      <Stack align="center" gap="xs" p="lg" style={{ textAlign: 'center' }}>
        <ThemeIcon size={44} radius="xl" variant="light" color="gray">
          <IconTextCaption size={22} />
        </ThemeIcon>
        <Text fw={600} size="sm">Transcript not available yet</Text>
        <Text size="xs" c="dimmed">It will appear here once processing is done.</Text>
      </Stack>,
    );
  }

  if (vtt.isLoading || txt.isLoading) {
    return frame(
      <Group justify="center" p="lg">
        <Loader size="sm" />
      </Group>,
    );
  }

  const plain = cues.length === 0 ? (txt.data ?? '').trim() : '';
  if (cues.length === 0 && !plain) {
    return frame(
      <Stack align="center" gap="xs" p="lg" style={{ textAlign: 'center' }}>
        <ThemeIcon size={44} radius="xl" variant="light" color="gray">
          <IconTextCaption size={22} />
        </ThemeIcon>
        <Text size="sm" c="dimmed">The transcript could not be loaded.</Text>
      </Stack>,
    );
  }

  const listHeight = compact ? 260 : '100%';

  return frame(
    <>
      <Stack gap={6} p="sm" style={{ flex: '0 0 auto' }}>
        {cues.length > 0 && (
          <TextInput
            size="xs"
            placeholder="Search the transcript"
            aria-label="Search the transcript"
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            leftSection={<IconSearch size={14} />}
            rightSection={
              query ? <CloseButton size="sm" aria-label="Clear search" onClick={() => setQuery('')} /> : null
            }
          />
        )}
        <Group justify="space-between" gap="xs" wrap="nowrap">
          <Text size="xs" c="dimmed">
            {q ? (visible.length === 0 ? 'No matches' : `${visible.length} ${visible.length === 1 ? 'match' : 'matches'}`) : ''}
          </Text>
          <Button
            size="compact-xs"
            variant="subtle"
            leftSection={<IconDownload size={12} />}
            onClick={download}
            loading={downloading}
          >
            Download transcript
          </Button>
        </Group>
      </Stack>

      <Box style={compact ? undefined : { flex: 1, minHeight: 0 }}>
        <ScrollArea
          h={listHeight}
          type="auto"
          offsetScrollbars
          scrollbarSize={6}
          viewportRef={viewportRef}
          onWheel={markUserScroll}
          onTouchMove={markUserScroll}
        >
          {cues.length > 0 ? (
            <Stack gap={2} px="xs" pb="sm">
              {visible.map(({ cue, index }) => (
                <CueRow
                  key={index}
                  cue={cue}
                  index={index}
                  active={index === activeIndex}
                  query={q}
                  seekable={!!controller}
                  onSeek={seek}
                  register={registerRow}
                />
              ))}
            </Stack>
          ) : (
            <Box px="sm" pb="sm">
              {plain.split(/\n{2,}|\n/).map((para, i) => (
                <Text key={i} size="sm" mb="xs">
                  {para}
                </Text>
              ))}
            </Box>
          )}
        </ScrollArea>
      </Box>
    </>,
  );
}

const CueRow = memo(function CueRow({
  cue,
  index,
  active,
  query,
  seekable,
  onSeek,
  register,
}: {
  cue: VttCue;
  index: number;
  active: boolean;
  query: string;
  seekable: boolean;
  onSeek: (cue: VttCue) => void;
  register: (index: number, el: HTMLElement | null) => void;
}) {
  const stamp = formatCueTime(cue.start);
  return (
    <UnstyledButton
      ref={(el: HTMLButtonElement | null) => register(index, el)}
      onClick={() => onSeek(cue)}
      disabled={!seekable}
      aria-label={`Jump to ${stamp}`}
      aria-current={active ? 'true' : undefined}
      data-cue-index={index}
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        padding: '6px 8px',
        borderRadius: 'var(--mantine-radius-sm)',
        background: active ? 'var(--mantine-color-brand-light)' : 'transparent',
        cursor: seekable ? 'pointer' : 'default',
        transition: 'background 120ms ease',
        width: '100%',
      }}
    >
      <Text
        size="xs"
        c={active ? 'brand' : 'dimmed'}
        fw={600}
        style={{ fontVariantNumeric: 'tabular-nums', flex: '0 0 auto', minWidth: 38, paddingTop: 2 }}
      >
        {stamp}
      </Text>
      <Highlight
        highlight={query}
        size="sm"
        fw={active ? 600 : 400}
        style={{ flex: 1, minWidth: 0, whiteSpace: 'pre-line' }}
      >
        {cue.text}
      </Highlight>
    </UnstyledButton>
  );
});
